import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CLOSE_DELAY_MS = 320;

/**
 * Botón "i" con panel tipo tooltip (hover + focus). Cerrar con Escape.
 * Opcional: `imageSrc` muestra un ejemplo en la burbuja.
 * El panel se renderiza en document.body con position:fixed para no quedar
 * recortado por overflow del formulario ni tapado por la columna del hero.
 */
function FieldTooltip({ label, title, imageSrc, imageAlt, children }) {
  const id = useId();
  const panelId = `${id}-panel`;
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const withImage = Boolean(imageSrc);

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearTimer();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setPanelStyle(null);
    }, CLOSE_DELAY_MS);
  };

  const updatePanelPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: Math.round(rect.bottom + 4),
      right: Math.round(window.innerWidth - rect.right),
      zIndex: 10050,
    });
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown]);

  useEffect(() => () => clearTimer(), []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }
    updatePanelPosition();
    const onMove = () => updatePanelPosition();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePanelPosition]);

  const panel = open && panelStyle && (
    <div
      id={panelId}
      role="tooltip"
      style={panelStyle}
      className={`tm-reg-tooltip-panel tm-reg-tooltip-panel--floating${withImage ? ' tm-reg-tooltip-panel--with-image' : ''}`}
      onMouseEnter={() => { clearTimer(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      {title && <p className="tm-reg-tooltip-title">{title}</p>}
      {withImage && (
        <div className="tm-reg-tooltip-img-wrap">
          <img src={imageSrc} alt={imageAlt || ''} className="tm-reg-tooltip-img" loading="lazy" />
        </div>
      )}
      <div className="tm-reg-tooltip-body">{children}</div>
    </div>
  );

  return (
    <span
      className="tm-reg-tooltip-wrap"
      ref={wrapRef}
      onMouseEnter={() => {
        clearTimer();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="tm-reg-tooltip-trigger"
        aria-label={label || 'Información'}
        aria-expanded={open}
        aria-controls={panelId}
        onFocus={() => {
          clearTimer();
          setOpen(true);
        }}
        onBlur={scheduleClose}
      >
        i
      </button>
      {panel && createPortal(panel, document.body)}
    </span>
  );
}

export default FieldTooltip;
